<template>
  <v-card class="invoice-list full-height" :flat="flat">
    <v-card-title v-if="showTitle" :class="{ 'py-2': shrinkWithoutInvoices && !hasInvoices, 'pb-0 pt-2': denseHeader && !(shrinkWithoutInvoices && !hasInvoices) }">
      <div class="card-title">Fakturor</div>
      <template v-if="loading">
        <v-progress-circular
          indeterminate
          size="40"
          class="ml-4"
          color="primary"
        ></v-progress-circular>
      </template>
      <template v-else>
        <v-alert
          v-show="shrinkWithoutInvoices && !hasInvoices"
          dense
          text
          color="info"
          class="ma-0 ml-4 py-1"
        >
          Det finns inga fakturor
        </v-alert>
      </template>
    </v-card-title>

    <div class="overflow-y-auto" :style="{ height: scrollableTableHeight }">
      <v-data-table
        v-show="hasInvoices || !shrinkWithoutInvoices"
        :headers="headers"
        :items="tableRows"
        @click:row="invoiceClicked"
        fixed-header
        :loading="loading"
        hide-default-footer
        disable-filtering
        disable-pagination
        :group-by="groupByBooking ? ['bookingId'] : []"
        :item-class="(item) => {
          return {
            'cursor-pointer': true,
            'warning-highlight': highlightUnpaidInvoices && item.remainingAmount > 0
          }
        }"
      >

        <template v-slot:no-data>
          <template v-if="loading">
            <v-simple-table class="skeleton-loader-table">
              <tr v-for="i in 2" :key="i">
                <td v-for="(header, x) in headers" :key="x" :class="{ 'text-left': header.align === 'start', 'text-right': header.align === 'end' }">
                  <v-skeleton-loader
                    style=""
                    type="table-cell"
                    class=""
                  ></v-skeleton-loader>
                </td>
              </tr>
            </v-simple-table>
          </template>
          <template v-else>
            <v-alert
              dense
              text
              color="info"
              class="ma-10 text-center"
            >
              Det finns inga fakturor att visa
            </v-alert>
          </template>
        </template>

        <template v-slot:[`group.header`]="{ group: bookingId, isOpen, toggle }">
          <td :colspan="headers.length" class="grey lighten-4 cursor-pointer" @click="toggle">
            <div class="d-flex full-width">
              <v-icon class="mr-3">{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
              <div class="subtitle-1 font-weight-medium my-auto">{{ getBookingTitle(bookingId) }}</div>
              <template v-if="getBookingIsDeleted(bookingId)">
                <v-chip
                  class="ml-2"
                  small
                  color="error"
                  text-color="white"
                >
                  Avbokad
                </v-chip>
              </template>
            </div>
          </td>
        </template>

        <template v-slot:[`item.remainingAmountFormatted`]="{ item: invoice }">
          <div :class="{ 'font-weight-black red--text': highlightUnpaidAmount && invoice.remainingAmount !== 0 }">
            {{ invoice.remainingAmountFormatted }}
          </div>
        </template>

        <template v-slot:[`item.statusText`]="{ item: invoice }">
          <div class="d-flex align-center">
            <v-chip
              class="justify-center"
              small
              :color="invoice.associatedOta ? 'blue-grey' : invoice.statusColor"
              text-color="white"
              style="min-width: 88px;"
            >
              {{ invoice.associatedOta || invoice.statusText }}
            </v-chip>
            <template v-if="invoice.belongsToBookingGroup">
              <simple-tooltip tooltip="Denna faktura tillhör en gruppbokning">
                <v-icon
                  class="ml-2"
                  small
                  color="pink"
                >mdi-calendar-multiple</v-icon>
              </simple-tooltip>
            </template>
          </div>
        </template>

        <template v-if="showTotalRow && hasInvoices" v-slot:[`body.append`]>
          <tr class="invoice-list-summary-row">
            <td
              v-for="header in headers"
              :key="header.value"
              :class="getSummaryCellClass(header)"
            >
              {{ getSummaryCellValue(header) }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.invoice-list-summary-row td {
  background-color: #fafafa;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 500;
}
</style>

<script>

import SimpleTooltip from '@/components/common/SimpleTooltip.vue'

export default {
  name: 'InvoiceList',
  components: {
    SimpleTooltip
  },
  props: {
    invoices: Array,
    loading: Boolean,
    showTitle: Boolean,
    denseHeader: Boolean,
    flat: Boolean,
    shrinkWithoutInvoices: Boolean,
    highlightUnpaidAmount: Boolean,
    showTotalRow: {
      type: Boolean,
      default: false
    },
    groupByBooking: {
      type: Boolean,
      default: false
    },
    bookingGroup: {
      type: Object, // Används bara om groupByBooking är true
      default: null
    },
    bookings: {
      type: Array, // Används bara om groupByBooking är true
      default: () => []
    },
    // TODO: blivit av med stödet för displayedItems, fixa
    displayedItems: { // antal fakturor som visas innan det blir scroll, 0 för obegränsat
      type: Number,
      default: function () { return 0 }
    },
    displayedColumns: {
      type: Array,
      default: function () { return ['invoice_number', 'invoice_date'] } // möjliga värden: invoice_number, ocr, customer_name, total, remaining_amount, invoice_date, due_date, status
    }
  },
  data: () => ({
    highlightUnpaidInvoices: false
  }),
  computed: {
    triggerUnpaidInvoiceAlert: function () {
      return this.$store.state.posCart.triggerUnpaidInvoiceAlert
    },
    headers: function () {
      const columns = []
      if (this.displayedColumns.includes('invoice_number')) {
        columns.push({ text: 'Fakturanummer', value: 'invoiceNumber', align: 'start', sortable: false })
      }
      if (this.displayedColumns.includes('ocr')) {
        columns.push({ text: 'OCR', value: 'ocr', align: 'start', sortable: false })
      }
      if (this.displayedColumns.includes('customer_name')) {
        columns.push({ text: 'Kund', value: 'customerName', align: 'start', sortable: false })
      }
      if (this.displayedColumns.includes('total')) {
        columns.push({ text: 'Fakturabelopp', align: 'end', value: 'totalFormatted', sortable: false })
      }
      if (this.displayedColumns.includes('remaining_amount')) {
        columns.push({ text: 'Återstående', align: 'end', value: 'remainingAmountFormatted', sortable: false })
      }
      if (this.displayedColumns.includes('invoice_date')) {
        columns.push({ text: 'Fakturadatum', value: 'createdAtFormatted', sortable: false })
      }
      if (this.displayedColumns.includes('due_date')) {
        columns.push({ text: 'Förfallodatum', value: 'dueDateFormatted', sortable: false })
      }
      if (this.displayedColumns.includes('status')) {
        columns.push({ text: 'Status', value: 'statusText', sortable: false })
      }
      return columns
    },
    tableRows: function () {
      return this.invoices.map(invoice => {
        return {
          invoiceId: invoice.id,
          bookingId: invoice.booking_id,
          invoiceNumber: invoice.invoice_number,
          ocr: invoice.ocr,
          customerName: invoice.customer_name,
          totalFormatted: invoice.totalFormatted,
          remainingAmount: invoice.remaining_amount,
          remainingAmountFormatted: invoice.remainingAmountFormatted,
          createdAtFormatted: invoice.createdAtFormatted,
          dueDateFormatted: invoice.dueDateFormatted,
          statusText: invoice.statusText,
          statusColor: invoice.statusColor,
          associatedOta: invoice.associated_ota,
          belongsToBookingGroup: !!invoice.booking_group_id
        }
      })
    },
    totalAmountSummary: function () {
      return this.invoices.reduce((totalAmount, invoice) => {
        return totalAmount + (invoice.total_amount || 0)
      }, 0)
    },
    totalAmountSummaryFormatted: function () {
      return window.conversion.toHumanReadableAmount(this.totalAmountSummary / 100)
    },
    remainingAmountSummary: function () {
      return this.invoices.reduce((remainingAmount, invoice) => {
        return remainingAmount + (invoice.remaining_amount || 0)
      }, 0)
    },
    remainingAmountSummaryFormatted: function () {
      return window.conversion.toHumanReadableAmount(this.remainingAmountSummary / 100)
    },
    summaryLabelColumnValue: function () {
      const labelColumn = this.headers.find(header => {
        return !['totalFormatted', 'remainingAmountFormatted'].includes(header.value)
      })
      return labelColumn?.value || null
    },
    invoiceCountSummary: function () {
      const invoiceCount = this.invoices.length
      return `${invoiceCount} ${invoiceCount === 1 ? 'faktura' : 'fakturor'}`
    },
    hasInvoices: function () {
      return this.invoices.length > 0
    },
    scrollableTableHeight: function () {
      if (!this.hasInvoices && this.showTitle) {
        return '0px'
      }
      if (this.displayedItems === 0) {
        return '100%'
      }
      let requiredHeight = this.invoices.length * 48
      if (this.groupByBooking) {
        const uniqueBookingIds = new Set(this.invoices.map(inv => inv.booking_id))
        requiredHeight += uniqueBookingIds.size * 48 // lägger till höjden för gruppheaders
      }
      const maxHeight = this.displayedItems * 48
      let height = Math.min(requiredHeight, maxHeight)
      if (requiredHeight > maxHeight) {
        height += 24 // lägger till en halv rad så man tydligt ser att det finns fler fakturor att scrolla ner till
      }
      height += 48 // lägger till header höjden
      return `${height}px`
    }
  },
  methods: {
    invoiceClicked: function ({ invoiceId }) {
      this.openInvoice(invoiceId)
    },
    openInvoice: function (invoiceId) {
      this.$store.commit('invoice/SET_SHOW_DIALOG', false)
      this.$nextTick(() => {
        this.$store.dispatch('invoice/openDialogById', { invoiceId })
      })
    },
    getSummaryCellValue: function (header) {
      if (header.value === this.summaryLabelColumnValue) {
        return `Summa (${this.invoiceCountSummary})`
      }
      if (header.value === 'totalFormatted') {
        return this.totalAmountSummaryFormatted
      }
      if (header.value === 'remainingAmountFormatted') {
        return this.remainingAmountSummaryFormatted
      }
      return ''
    },
    getSummaryCellClass: function (header) {
      return {
        'text-left': header.align === 'start' || header.value === this.summaryLabelColumnValue,
        'text-right': header.align === 'end' || ['totalFormatted', 'remainingAmountFormatted'].includes(header.value)
      }
    },
    getBookingTitle: function (bookingId) {
      if (!bookingId) {
        return 'Fakturor på gruppbokningen'
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return `Bokning #${bookingId}`
      }
      if (!this.bookingGroup?.hasMultipleCustomers) {
        // Behöver inte skriva ut kundnamnet om alla bokningar har samma kund
        return `${booking.booking_number}, ${booking.position.full_name}`
      }
      return `${booking.booking_number}, ${booking.position.full_name}, ${booking.customer.full_name}`
    },
    getBookingIsDeleted: function (bookingId) {
      if (!bookingId) {
        return false
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return false
      }
      return !!booking.deleted_at
    }
  },
  watch: {
    triggerUnpaidInvoiceAlert: function () {
      this.highlightUnpaidInvoices = true
      setTimeout(() => {
        this.highlightUnpaidInvoices = false
      }, 2000)
    }
  }
}
</script>
